import { z } from 'zod';
import { getTrafficDataFromSearchParams } from './utilities';

declare global {
  interface Window {
    /** Set by `LeadBrowserContextBridge` for the Act-On scripts in `public/js`. */
    getLeadBrowserContext?: typeof getLeadBrowserContext;
  }
}

const ClickIds = z.array(z.object({ value: z.string() })).optional();

/**
 * Mosaic stores every click ID it saw in the analytics user traits; see `persistAttributionParams`.
 * `traffic-source-tracking` stores the sourcebuster attribution there as `trafficInfo`.
 */
const StoredTraitsSchema = z.object({
  adAttribution: z.object({ gclid: ClickIds, fbclid: ClickIds }).optional(),
  trafficInfo: z
    .object({
      type: z.string(),
      source: z.string(),
      medium: z.string(),
      campaign: z.string(),
      content: z.string(),
      term: z.string(),
    })
    .optional(),
});

/** Traffic attribution in the shape of the Lead Service `Lead.Traffic`. */
export type LeadTraffic = {
  Source: string;
  Medium: string;
  Campaign: string;
  Content: string;
  Term: string;
  Type: string;
};

/** Parses the `sbjs_current` cookie, e.g. `typ=utm|||src=google|||mdm=cpc|||cmp=google_cpc|||cnt=(none)|||trm=(none)`. */
function readSourcebusterCookie(): LeadTraffic | undefined {
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('sbjs_current='))
    ?.slice('sbjs_current='.length);
  if (!raw) return undefined;
  const fields = new Map(
    decodeURIComponent(raw)
      .split('|||')
      .map((pair) => {
        const i = pair.indexOf('=');
        if (i < 0) return ['', ''] as const;
        return [pair.slice(0, i), pair.slice(i + 1)] as const;
      })
  );
  const type = fields.get('typ');
  const source = fields.get('src');
  const medium = fields.get('mdm');
  if (!type || !source || !medium) return undefined;
  return {
    Source: source,
    Medium: medium,
    Campaign: fields.get('cmp') ?? '(none)',
    Content: fields.get('cnt') ?? '(none)',
    Term: fields.get('trm') ?? '(none)',
    // Matches `mapType` in `traffic-source-tracking/setup-source-buster.ts`.
    Type: type === 'typein' ? 'direct' : type,
  };
}

/**
 * Traffic attribution for a lead that the site itself builds, such as the Web2Text widget.
 *
 * A `utm_source` in the current URL wins, because sourcebuster runs only on a full page load and
 * misses a client-side navigation. Otherwise the sourcebuster attribution from the analytics traits
 * or the `sbjs_current` cookie keeps the landing attribution across pages and maps `gclid` to
 * `google / cpc`. Without either, the result is `direct / none`.
 */
export function getLeadTraffic(): LeadTraffic {
  const params = new URLSearchParams(window.location.search);
  const fromUrl: LeadTraffic = getTrafficDataFromSearchParams(params);
  if (params.has('utm_source')) {
    return fromUrl;
  }
  try {
    const stored = StoredTraitsSchema.safeParse(
      JSON.parse(localStorage.getItem('ajs_user_traits') ?? 'null')
    ).data?.trafficInfo;
    if (stored) {
      return {
        Source: stored.source,
        Medium: stored.medium,
        Campaign: stored.campaign,
        Content: stored.content,
        Term: stored.term,
        Type: stored.type,
      };
    }
    return readSourcebusterCookie() ?? fromUrl;
  } catch {
    return fromUrl;
  }
}

/**
 * Lead details that only the visitor's browser knows. The lead routes drop the values
 * the Lead Service would reject, so this does no validation.
 */
export type LeadBrowserContext = {
  CustomerInformation?: { Locale?: string; Timezone?: string };
  SubmissionContext?: {
    Referrer?: string;
    Traffic?: { Gclid?: string; Fbclid?: string };
  };
};

/**
 * Collects the browser-only lead details to send with a lead to `/api/leads` or `/api/web2Text/service`.
 * Returns `{}` on failure, because these details must never block the lead itself.
 */
export function getLeadBrowserContext(): LeadBrowserContext {
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = StoredTraitsSchema.safeParse(
      JSON.parse(localStorage.getItem('ajs_user_traits') ?? 'null')
    ).data?.adAttribution;
    // The Lead Service accepts only `language[-REGION]`, e.g. `es-419` becomes `es`.
    const { language, region } = new Intl.Locale(navigator.language);
    return {
      CustomerInformation: {
        Locale: region?.length === 2 ? `${language}-${region}` : language,
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      SubmissionContext: {
        Referrer: document.referrer,
        Traffic: {
          Gclid: params.get('gclid') ?? stored?.gclid?.at(-1)?.value,
          Fbclid: params.get('fbclid') ?? stored?.fbclid?.at(-1)?.value,
        },
      },
    };
  } catch {
    return {};
  }
}
